import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  DataTable,
  Field,
  FormGrid,
  FormGridItem,
  InlineMessage,
  Stack,
  StatTile,
  Text,
  Textarea,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { formatCurrency } from '../objects/format';
import { NumberSetting } from './fields';
import { type PricePoint, pricesConsidered, pricingInputs, type PricingPolicy, recommend } from '@quote-math/pricingAdvice';
import type { QuoteBuilder } from './quotesApi';
import type { ValueModel } from '@quote-math/valueModel';

const money = (n: number) => formatCurrency(Math.round(n));
const vsList = (value: number, list: number) => {
  if (!list) return '';
  const pct = Math.round(((value - list) / list) * 1000) / 10;
  return pct === 0 ? 'At list' : pct > 0 ? `+${pct}% over list` : `${Math.abs(pct)}% off list`;
};
const paybackText = (p: number | null) => (p === null ? 'No payback' : `${p < 1 ? 'Under 1' : Math.round(p * 10) / 10} months`);

/**
 * Pricing: what to ask for. The recommendation prices the one-time work to
 * the value model within the policy's guardrails; "Why this price" shows
 * every price considered. Applying it sets the order's one-time target price.
 */
export function PricingTab({
  data,
  model,
  busy,
  onApply,
  onSavePolicy,
}: {
  data: QuoteBuilder;
  model: ValueModel;
  busy: boolean;
  onApply: (oneTime: number, notes: string[]) => void;
  onSavePolicy: (patch: Partial<PricingPolicy>) => void;
}): JSX.Element {
  const policy = data.policy;
  const [philosophy, setPhilosophy] = useState(policy?.philosophy ?? '');
  useEffect(() => setPhilosophy(policy?.philosophy ?? ''), [policy?.philosophy]);
  if (!policy) return <InlineMessage tone="warning">The pricing policy is not set up yet.</InlineMessage>;

  const inputs = pricingInputs(data);
  const rec = recommend(model, policy, inputs);
  const points = pricesConsidered(model, policy, inputs, rec);
  const current = data.totals.oneTime.net;
  const applied = Math.abs(current - rec.oneTime) < 1;

  const columns: ColumnDef<PricePoint>[] = [
    { key: 'label', label: 'Price', width: 220, get: (p) => (p.recommended ? `${p.label} (recommended)` : p.label) },
    { key: 'oneTime', label: 'One-Time', align: 'right', nowrap: true, render: (p) => money(p.oneTime) },
    { key: 'vs', label: 'Against List', nowrap: true, get: (p) => vsList(p.oneTime, inputs.list) },
    { key: 'payback', label: 'Payback', nowrap: true, get: (p) => paybackText(p.payback) },
    { key: 'return3', label: '3-Year Return', align: 'right', nowrap: true, get: (p) => `${Math.round(p.return3 * 10) / 10}x` },
    { key: 'verdict', label: 'What It Means', width: 420, get: (p) => p.verdict },
  ];

  return (
    <Stack>
      <Card>
        <CardHeader
          title="Recommendation"
          actions={
            <Button variant="brand" disabled={busy || applied || !inputs.list} onClick={() => onApply(rec.oneTime, rec.notes)}>
              {applied ? 'Applied to order' : 'Apply to order'}
            </Button>
          }
        />
        <CardBody>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatTile label="One-Time Services" value={money(rec.oneTime)} />
            <StatTile label="Against List" value={vsList(rec.oneTime, inputs.list) || '—'} />
            <StatTile label="Payback" value={paybackText(rec.payback)} />
            <StatTile label="Monthly" value={`${money(rec.monthly)} / mo`} />
          </div>
          <ul className="mt-2 text-sm">
            {rec.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
          <Text tone="muted">
            On the order now: {money(current)} one-time. Applying sets the order's one-time discount to a target of {money(rec.oneTime)}.
          </Text>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Why this price" />
        <DataTable<PricePoint> columns={columns} rows={points} getRowId={(p) => p.label} density="compact" />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Guardrails" />
          <CardBody>
            <Text tone="muted">Shared by every quote.</Text>
            <FormGrid>
              <FormGridItem>
                <NumberSetting label="Share of 3-Year Value (%)" value={policy.value_share_pct} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ value_share_pct: v })} />
              </FormGridItem>
              <FormGridItem>
                <NumberSetting label="Return Target (x)" value={policy.roi_target} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ roi_target: v })} />
              </FormGridItem>
              <FormGridItem>
                <NumberSetting label="Largest Premium (%)" value={policy.max_premium_pct} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ max_premium_pct: v })} />
              </FormGridItem>
              <FormGridItem>
                <NumberSetting label="Largest Discount (%)" value={policy.max_discount_pct} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ max_discount_pct: v })} />
              </FormGridItem>
              <FormGridItem>
                <NumberSetting label="Hourly Floor ($)" value={policy.hourly_floor} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ hourly_floor: v })} />
              </FormGridItem>
              <FormGridItem>
                <NumberSetting label="Payback Target (Months)" value={policy.target_payback_months} disabled={busy} onSave={(v) => v !== null && onSavePolicy({ target_payback_months: v })} />
              </FormGridItem>
            </FormGrid>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Pricing philosophy" />
          <CardBody>
            <Field label="How Olytic prices; shared by every quote">
              <Textarea
                rows={14}
                value={philosophy}
                disabled={busy}
                onChange={(e) => setPhilosophy(e.target.value)}
                onBlur={() => philosophy !== (policy.philosophy ?? '') && onSavePolicy({ philosophy })}
              />
            </Field>
          </CardBody>
        </Card>
      </div>
    </Stack>
  );
}
